// PARTIAL SHIM — the wire half of this module moved to
// `@heidi/evidence/contracts` with the `evidence/guidance` carve and is
// re-exported below name-intact, so every existing importer of this path keeps
// resolving. The FORM half stayed here on purpose.
//
// The line between them is `zod`. The package's contracts zone is inert by
// fixture — relative imports and `@heidi/schema` only — because generated type
// text cannot carry a runtime into it. `createHealthPathwaysSetupSchema` is a
// localized zod factory whose messages the caller injects, its postcode table
// is a validation detail rather than a wire fact, and its only consumer is the
// setup dialog. A form schema belongs beside its component, so it stays and the
// inertness fixture keeps its measured width.
import { z } from 'zod';

export {
  HEALTHPATHWAYS_ZONE_NOT_SUPPORTED_CODE,
  type HealthPathwaysSetupConfig,
  type HealthPathwaysSetupRequest,
  type HealthPathwaysSetupResponse,
} from '@heidi/evidence/contracts';

/** Supported HealthPathways country codes */
export const HEALTHPATHWAYS_COUNTRIES = ['au', 'nz', 'gb', 'ca'] as const;

// First-pass shape check so obvious typos (e.g. an AU postcode entered into a
// GB form, or a 7-digit number) fail fast in the dialog. The upstream MCP
// silently falls back to a major-city zone for anything containing ≥1 digit,
// so without this the user wouldn't know they'd been routed to Sydney/Auckland.
// True existence is verified BE-side via pgeocode before calling the MCP.
const POSTCODE_PATTERNS: Record<string, RegExp> = {
  au: /^\d{4}$/,
  nz: /^\d{4}$/,
  gb: /^[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}$/i,
  ca: /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/,
};

/** Create a localized Zod schema for the setup form */
export function createHealthPathwaysSetupSchema(messages: {
  countryRequired: string;
  postcodeRequired: string;
  postcodeInvalidFormat: string;
}) {
  return z
    .object({
      country_code: z.string().min(1, messages.countryRequired),
      postcode: z.string().min(1, messages.postcodeRequired),
      zone_type: z.enum(['hospital', 'community']),
    })
    .superRefine((data, ctx) => {
      const trimmed = data.postcode.trim();
      if (!trimmed) return; // `min(1)` already surfaced; don't double-report
      const pattern = POSTCODE_PATTERNS[data.country_code.toLowerCase()];
      if (pattern && !pattern.test(trimmed)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['postcode'],
          message: messages.postcodeInvalidFormat,
        });
      }
    });
}

export type HealthPathwaysSetupFormValues = z.infer<
  ReturnType<typeof createHealthPathwaysSetupSchema>
>;
